import React, { useEffect, useState } from "react";
import axios from '../Axiosapi';
import env_constants from '../Constants';
import { toast } from 'react-toastify';
import { Helmet } from "react-helmet-async";

const envConstants = env_constants;

const Contactus = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        subject: '',
        message: ''
    });
    const [submitBtnText, setSubmitBtnText] = useState('Submit');
    const [submitBtnDisable, setSubmitBtnDisable] = useState(false);
    const [socialLinks, setSocialLinks] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const apiUrl = `${env_constants.API_BASE_URL}/contact-us`;

        const payload = {
            f_name: formData.firstName,
            l_name: formData.lastName,
            email: formData.email,
            phone: formData.phoneNumber,
            subject: formData.subject,
            message: formData.message,
        };

        try {
            setSubmitBtnText('Submitting...');
            setSubmitBtnDisable(true);
            const response = await axios.post(apiUrl, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Form Submitted Successfully');
            setSubmitBtnText('Submit');
            setSubmitBtnDisable(false);
            // Clear form fields
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            toast.error(`Error submitting form: ${error.message}`);
            setSubmitBtnText('Submit');
            setSubmitBtnDisable(false);
        }
    };

    useEffect(() => {
        const fetchSocialLinks = async () => {
            try {
                const response = await axios.get(`${envConstants.API_BASE_URL}/social-links`);
                setSocialLinks(response.data.data || {});
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        fetchSocialLinks();
    }, []);

    const canonicalUrl = 'https://autopartstation.com.au/contact-us';
    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Contact Us - Auto Part Station",
        "url": "https://autopartstation.com.au/contact-us",
        "mainEntity": {
            "@type": "Organization",
            "name": "Auto Part Station Pty Ltd",
            "url": "https://autopartstation.com.au",
            "logo": "https://autopartstation.com.au/auto-part.png"
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact Auto Part Station - Get in Touch for Assistance</title>
                <meta name="description" content="Get in touch with Auto Part Station for inquiries or support. We're here to help you with your auto parts needs!" />
                <meta name="author" content={envConstants.STORE_META_AUTHOR ? envConstants.STORE_META_AUTHOR : 'Auto Part Station Store'} />
                <meta name="keywords" content="Auto Part Station contact us, Auto Part Station support" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(schemaMarkup)}
                </script>
            </Helmet>

            <div className="container py-4">
                <div className="row">
                    {/* Contact Information */}
                    <div className="col-lg-5 mb-5 mb-lg-0">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body p-4">
                                <h2 className="h4 mb-4 border-bottom pb-2">How to Reach Us</h2>

                                <div className="d-flex mb-4">
                                    <div className="flex-shrink-0">
                                        <i className="bi bi-envelope-fill fs-4 text-primary"></i>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-1">Email</h5>
                                        <p className="mb-0">
                                            <a href="mailto:sales@autopartstation.com.au" className="text-decoration-none text-dark">sales@autopartstation.com.au</a>
                                        </p>
                                        <small className="text-muted">We respond within 24 hours</small>
                                    </div>
                                </div>

                                <div className="d-flex mb-4">
                                    <div className="flex-shrink-0">
                                        <i className="bi bi-telephone-fill fs-4 text-primary"></i>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-1">Phone</h5>
                                        <p className="mb-0">
                                            <a href="tel:+61490533318" className="text-decoration-none text-dark">+61 490 533 318</a>
                                        </p>
                                    </div>
                                </div>

                                <div className="d-flex mb-4">
                                    <div className="flex-shrink-0">
                                        <i className="bi bi-clock-fill fs-4 text-primary"></i>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-1">Operating Hours</h5>
                                        <p className="mb-0">Monday – Sunday | 8:00 AM – 6:00 PM AEST</p>
                                    </div>
                                </div>

                                <div className="d-flex mb-4">
                                    <div className="flex-shrink-0">
                                        <i className="bi bi-geo-alt-fill fs-4 text-primary"></i>
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-1">Mailing Address</h5>
                                        <address className="mb-0">
                                            <a className="text-decoration-none text-dark" href="https://www.google.com/maps/place/G-Tech+Solutions+%7C+Custom+Software+Development+Agency/@-33.8979257,151.0359381,1165m/data=!3m2!1e3!4b1!4m6!3m5!1s0x6b12aeed42b5aff3:0x7fa34753854d97c2!8m2!3d-33.8979302!4d151.0385184!16s%2Fg%2F11h04mkv3d?entry=tts&g_ep=EgoyMDI1MTAyOS4yIPu8ASoASAFQAw%3D%3D&skid=2aa98915-32a8-4cae-a994-fd51c7e7e79d" target="_blank">
                                                101 Rookwood Road Yagoona – NSW 2199
                                            </a>
                                        </address>
                                    </div>
                                </div>

                                <h5 className="mt-4 mb-3">Follow Us</h5>
                                <div className="tab__content">
                                    <ul className="social-icon d-flex gap-3">
                                        {socialLinks?.links?._facebook_link && (
                                            <li>
                                                <a href={socialLinks?.links?._facebook_link} className="facebook social-link" target="_blank" rel="noopener noreferrer"></a>
                                            </li>
                                        )}
                                        {socialLinks?.links?._instagram_link && (
                                            <li>
                                                <a href={socialLinks?.links?._instagram_link} target="_blank" rel="noopener noreferrer">
                                                    <svg width="15" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5.27263 0.0629441C4.31502 0.108124 3.66107 0.260945 3.08939 0.485586C2.49772 0.716166 1.99623 1.02559 1.49727 1.52635C0.998305 2.02711 0.691042 2.52895 0.462079 3.12151C0.240497 3.69446 0.0903755 4.34894 0.0480751 5.30708C0.00577466 6.26523 -0.00358544 6.57321 0.0010946 9.01725C0.00577465 11.4613 0.0165748 11.7677 0.0630152 12.7278C0.108736 13.6852 0.261017 14.339 0.485659 14.9108C0.716602 15.5025 1.02566 16.0038 1.52661 16.5029C2.02755 17.0021 2.52904 17.3086 3.12305 17.5379C3.69545 17.7592 4.35012 17.91 5.30809 17.9519C6.26606 17.9939 6.5744 18.0036 9.01775 17.9989C11.4611 17.9942 11.7687 17.9834 12.7287 17.9379C13.6886 17.8924 14.339 17.739 14.911 17.5154C15.5027 17.284 16.0043 16.9754 16.5031 16.4743C17.0019 15.9732 17.309 15.471 17.5378 14.8781C17.7595 14.3057 17.9102 13.651 17.9518 12.6938C17.9937 11.7331 18.0036 11.4264 17.9989 8.9827C17.9943 6.53901 17.9833 6.23265 17.9377 5.27288C17.8922 4.31312 17.7397 3.66134 17.5153 3.08911C17.284 2.49745 16.9753 1.99651 16.4745 1.49701C15.9737 0.997507 15.4712 0.690606 14.8784 0.462365C14.3057 0.240785 13.6514 0.0897642 12.6934 0.048364C11.7354 0.00696388 11.4271 -0.00365616 8.98283 0.00102385C6.53858 0.00570387 6.23258 0.0161439 5.27263 0.0629441ZM5.37775 16.3328C4.50024 16.2947 4.02378 16.1489 3.70625 16.0268C3.28577 15.8648 2.98624 15.669 2.6698 15.3556C2.35336 15.0422 2.15896 14.7416 1.99479 14.3221C1.87149 14.0045 1.72299 13.5286 1.68195 12.6511C1.63731 11.7027 1.62795 11.4179 1.62273 9.01509C1.61751 6.61227 1.62669 6.32787 1.66827 5.37908C1.70571 4.5023 1.85241 4.0253 1.97427 3.70796C2.13628 3.28694 2.3314 2.98795 2.6455 2.67169C2.9596 2.35543 3.25931 2.16067 3.67925 1.99651C3.99641 1.87267 4.47234 1.72543 5.34949 1.68367C6.29864 1.63867 6.58304 1.62967 8.98553 1.62445C11.388 1.61923 11.6731 1.62823 12.6226 1.66999C13.4994 1.70815 13.9766 1.85341 14.2936 1.97599C14.7143 2.13799 15.0136 2.33257 15.3299 2.64721C15.6461 2.96185 15.8411 3.26048 16.0052 3.68132C16.1293 3.99758 16.2765 4.47332 16.3179 5.351C16.3631 6.30015 16.3733 6.58473 16.3777 8.98701C16.382 11.3893 16.3735 11.6746 16.3319 12.623C16.2936 13.5005 16.1482 13.9772 16.0259 14.2951C15.8639 14.7154 15.6686 15.0151 15.3543 15.3311C15.0401 15.6472 14.7407 15.842 14.3206 16.0061C14.0038 16.1298 13.5273 16.2774 12.6509 16.3192C11.7018 16.3638 11.4173 16.3732 9.01397 16.3784C6.61058 16.3836 6.32708 16.3739 5.37793 16.3328M12.7148 4.18982C12.7152 4.40344 12.7789 4.61215 12.8978 4.78957C13.0168 4.96698 13.1858 5.10513 13.3833 5.18653C13.5808 5.26793 13.798 5.28894 14.0074 5.24689C14.2169 5.20484 14.4091 5.10162 14.5599 4.95029C14.7107 4.79896 14.8132 4.60632 14.8545 4.39673C14.8957 4.18714 14.8739 3.97001 14.7918 3.77281C14.7097 3.5756 14.5709 3.40719 14.3931 3.28885C14.2152 3.17052 14.0063 3.10759 13.7927 3.10801C13.5063 3.10859 13.2319 3.22287 13.0297 3.42574C12.8276 3.62861 12.7143 3.90345 12.7148 4.18982ZM4.37892 9.00898C4.38396 11.5614 6.45686 13.6258 9.00875 13.621C11.5606 13.6161 13.6265 11.5434 13.6217 8.99098C13.6168 6.43857 11.5434 4.3736 8.99111 4.37864C6.43886 4.38368 4.37406 6.45693 4.37892 9.00898ZM6.00002 9.00574C5.99884 8.41238 6.17365 7.832 6.50232 7.33799C6.831 6.84398 7.29879 6.45853 7.84653 6.23037C8.39428 6.00222 8.99737 5.94162 9.57956 6.05622C10.1617 6.17083 10.6969 6.4555 11.1173 6.87423C11.5377 7.29297 11.8245 7.82696 11.9414 8.40869C12.0583 8.99042 12.0001 9.59375 11.7741 10.1424C11.5481 10.691 11.1645 11.1603 10.6718 11.491C10.1791 11.8216 9.59941 11.9987 9.00605 11.9999C8.61205 12.0007 8.22176 11.9239 7.85745 11.7739C7.49314 11.6238 7.16196 11.4035 6.88281 11.1255C6.60367 10.8474 6.38203 10.5171 6.23056 10.1534C6.07909 9.78971 6.00075 9.39972 6.00002 9.00574Z" fill="#616161" />
                                                    </svg>
                                                </a>
                                            </li>
                                        )}
                                        {socialLinks?.links?._linke_din && (
                                            <li>
                                                <a href={socialLinks?.links?._linke_din} target="_blank" rel="noopener noreferrer">
                                                    <svg class="e-font-icon-svg e-fab-linkedin" viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M416 32H31.9C14.3 32 0 46.5 0 64.3v383.4C0 465.5 14.3 480 31.9 480H416c17.6 0 32-14.5 32-32.3V64.3c0-17.8-14.4-32.3-32-32.3zM135.4 416H69V202.2h66.5V416zm-33.2-243c-21.3 0-38.5-17.3-38.5-38.5S80.9 96 102.2 96c21.2 0 38.5 17.3 38.5 38.5 0 21.3-17.2 38.5-38.5 38.5zm282.1 243h-66.4V312c0-24.8-.5-56.7-34.5-56.7-34.6 0-39.9 27-39.9 54.9V416h-66.4V202.2h63.7v29.2h.9c8.9-16.8 30.6-34.5 62.9-34.5 67.2 0 79.7 44.3 79.7 101.9V416z" fill="#616161"></path>
                                                    </svg>
                                                </a>
                                            </li>
                                        )}
                                        <li>
                                            <a href="https://pin.it/5u6kjdDQR" target="_blank" rel="noopener noreferrer">
                                                <svg width="16" height="16" viewBox="0 0 529 529" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M186.162 516.706C211.548 524.374 237.198 528.87 264.435 528.87C334.567 528.87 401.827 501.009 451.418 451.418C501.009 401.827 528.87 334.567 528.87 264.435C528.87 229.709 522.03 195.323 508.741 163.24C495.452 131.157 475.973 102.006 451.418 77.4511C426.863 52.8961 397.712 33.418 365.63 20.1289C333.547 6.83981 299.161 0 264.435 0C229.709 0 195.323 6.83981 163.24 20.1289C131.157 33.418 102.006 52.8961 77.4511 77.4511C27.86 127.042 0 194.302 0 264.435C0 376.82 70.6041 473.338 170.296 511.417C167.916 490.791 165.536 456.679 170.296 433.144L200.706 302.513C200.706 302.513 193.037 287.176 193.037 262.848C193.037 226.356 215.779 199.119 241.693 199.119C264.435 199.119 275.012 215.779 275.012 237.198C275.012 259.939 259.939 292.465 252.271 323.668C247.775 349.583 266.021 372.324 292.465 372.324C339.534 372.324 376.026 322.082 376.026 251.213C376.026 187.749 330.543 144.381 265.228 144.381C190.657 144.381 146.761 199.913 146.761 258.353C146.761 281.094 154.165 304.1 166.329 319.173C168.709 320.759 168.709 322.875 167.916 326.841L160.247 355.665C160.247 360.16 157.339 361.747 152.843 358.574C118.996 343.765 99.4275 295.638 99.4275 256.766C99.4275 173.205 158.661 97.312 272.897 97.312C363.862 97.312 434.731 162.627 434.731 249.362C434.731 340.328 378.406 413.311 297.754 413.311C272.103 413.311 246.982 399.561 237.991 383.43L220.274 446.101C214.192 468.843 197.533 499.253 186.162 517.499V516.706Z" fill="#616161" />
                                                </svg>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="col-lg-7">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-4">
                                <h2 className="h4 mb-4 border-bottom pb-2">Send Us a Message</h2>
                                <p className="text-muted mb-4">Have questions about products, orders, or need technical support? Fill out the form below and we'll get back to you as soon as possible.</p>

                                <form onSubmit={handleSubmit}>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <div className="form-floating mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="firstName"
                                                    placeholder="First Name"
                                                    name="firstName"
                                                    value={formData.firstName}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="firstName">First Name</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-floating mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="lastName"
                                                    placeholder="Last Name"
                                                    name="lastName"
                                                    value={formData.lastName}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="lastName">Last Name</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-floating mb-3">
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    placeholder="Email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="email">Email</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-floating mb-3">
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    id="phoneNumber"
                                                    placeholder="Phone Number"
                                                    name="phoneNumber"
                                                    value={formData.phoneNumber}
                                                    onChange={handleChange}
                                                />
                                                <label htmlFor="phoneNumber">Phone Number</label>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-floating mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="subject"
                                                    placeholder="Subject"
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    required
                                                />
                                                <label htmlFor="subject">Subject</label>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="form-floating mb-3">
                                                <textarea
                                                    className="form-control"
                                                    placeholder="Message"
                                                    id="message"
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    style={{ height: "150px" }}
                                                    required
                                                ></textarea>
                                                <label htmlFor="message">Message</label>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <button
                                                className="btn btn-lg w-100 text-white"
                                                style={{ backgroundColor: '#ed1c24' }}
                                                type="submit"
                                                disabled={submitBtnDisable}
                                            >
                                                {submitBtnText}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Contactus;